import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Network, Plus, Minus, Circle } from "lucide-react";

/**
 * Componente MindMap - Estilo MindMeister
 * Exibe a hierarquia de tópicos em formato radial/visual similar ao MindMeister
 */
const MindMap = ({ mindMapData }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  if (!mindMapData || !mindMapData.topics) {
    return (
      <Card className="card">
        <CardContent className="p-8 text-center">
          <Network className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Mapa mental não disponível para este vídeo</p>
        </CardContent>
      </Card>
    );
  }

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  // Cores e estilos por nível (estilo MindMeister)
  const getNodeStyle = (level) => {
    const styles = {
      1: {
        bg: 'bg-gradient-to-br from-blue-500 to-blue-600',
        text: 'text-white',
        border: 'border-blue-600',
        shadow: 'shadow-lg shadow-blue-200',
        size: 'min-w-[200px] px-6 py-4',
        fontSize: 'text-lg font-bold',
        lineColor: 'bg-blue-400'
      },
      2: {
        bg: 'bg-gradient-to-br from-purple-400 to-purple-500',
        text: 'text-white',
        border: 'border-purple-500',
        shadow: 'shadow-md shadow-purple-100',
        size: 'min-w-[160px] px-5 py-3',
        fontSize: 'text-base font-semibold',
        lineColor: 'bg-purple-300'
      },
      3: {
        bg: 'bg-gradient-to-br from-teal-400 to-teal-500',
        text: 'text-white',
        border: 'border-teal-500',
        shadow: 'shadow-md shadow-teal-100',
        size: 'min-w-[140px] px-4 py-2.5',
        fontSize: 'text-sm font-medium',
        lineColor: 'bg-teal-300'
      }
    };
    return styles[level] || styles[3];
  };

  // Renderiza um nó do mapa mental
  const renderNode = (topic, parentLevel = 0) => {
    const hasChildren = topic.subtopics && topic.subtopics.length > 0;
    const isExpanded = expandedNodes.has(topic.id);
    const style = getNodeStyle(topic.level);

    return (
      <div key={topic.id} className="relative">
        {/* Nó principal */}
        <div className="flex items-center gap-4">
          {/* Botão de expandir/colapsar */}
          {hasChildren && (
            <button
              onClick={() => toggleNode(topic.id)}
              className={`flex-shrink-0 w-6 h-6 rounded-full ${style.bg} ${style.text} flex items-center justify-center hover:scale-110 transition-transform ${style.shadow}`}
            >
              {isExpanded ? (
                <Minus className="w-3 h-3" />
              ) : (
                <Plus className="w-3 h-3" />
              )}
            </button>
          )}
          
          {hasChildren === false && (
            <Circle className={`flex-shrink-0 w-2 h-2 ${style.text.replace('text-', 'fill-')}`} />
          )}

          {/* Card do tópico */}
          <div
            className={`
              ${style.size} ${style.bg} ${style.text} ${style.shadow}
              rounded-2xl border-2 ${style.border}
              transition-all duration-300 hover:scale-105 cursor-pointer
              relative overflow-hidden group
            `}
            onClick={() => hasChildren && toggleNode(topic.id)}
          >
            {/* Efeito de brilho */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
            
            <div className="relative z-10">
              <h4 className={`${style.fontSize} leading-tight`}>
                {topic.title}
              </h4>
              
              {/* Contador de subtópicos */}
              {hasChildren && (
                <div className="mt-2 flex items-center gap-1.5 text-xs opacity-90">
                  <Circle className="w-1.5 h-1.5 fill-current" />
                  <span>{topic.subtopics.length} {topic.subtopics.length === 1 ? 'subtópico' : 'subtópicos'}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subtópicos (renderizado se expandido) */}
        {hasChildren && isExpanded && (
          <div className="ml-12 mt-4 space-y-4 relative">
            {/* Linha de conexão vertical */}
            <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${style.lineColor} rounded-full`} />
            
            {topic.subtopics.map((subtopic, index) => (
              <div key={subtopic.id} className="relative pl-8">
                {/* Linha de conexão horizontal */}
                <div className={`absolute left-0 top-1/2 w-8 h-0.5 ${style.lineColor} rounded-full`} />
                
                {renderNode(subtopic, topic.level)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Inicializa todos os nós de nível 1 como expandidos
  React.useEffect(() => {
    if (mindMapData && mindMapData.topics) {
      const level1Ids = mindMapData.topics.map(t => t.id);
      setExpandedNodes(new Set(level1Ids));
    }
  }, [mindMapData]);

  return (
    <Card className="card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Network className="w-5 h-5 text-blue-600" />
          {mindMapData.title || "Mapa Mental do Conteúdo"}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-4 mb-8">
          <p className="text-sm text-blue-800">
            🧠 <strong>Dica:</strong> Clique nos botões <Plus className="inline w-3 h-3" />/<Minus className="inline w-3 h-3" /> ou nos cards para expandir/recolher tópicos
          </p>
        </div>
        
        {/* Nó central (raiz) */}
        <div className="flex flex-col items-center">
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white px-8 py-5 rounded-3xl shadow-2xl shadow-indigo-300 border-4 border-indigo-500 mb-12 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/20 to-white/30 opacity-0 group-hover:opacity-100 transition-opacity" />
            <h3 className="text-2xl font-bold text-center relative z-10 flex items-center gap-3">
              <Network className="w-7 h-7" />
              {mindMapData.title || "Conteúdo do Vídeo"}
            </h3>
          </div>

          {/* Tópicos principais */}
          <div className="w-full space-y-6">
            {mindMapData.topics.map((topic, index) => (
              <div key={topic.id} className="relative">
                {/* Linha conectando ao nó central */}
                <div className="absolute left-1/2 -top-12 w-0.5 h-12 bg-gradient-to-b from-indigo-400 to-blue-400 rounded-full transform -translate-x-1/2" />
                
                <div className="flex justify-center">
                  <div className="w-full max-w-4xl">
                    {renderNode(topic)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MindMap;
